import { type CSSObject, type Theme } from "@mui/material/styles";

import { type ComponentOverrides } from "theme/theme.types";

type ButtonSizeName = "small" | "medium" | "large";

/**
 * Spacing multipliers. **No height, by design** — the box is `line-height + 2 * py`, so 24 / 32 / 40 fall
 * out of these numbers, which is why the label's absolute line-height (`typography.button*`) is
 * load-bearing: `typography.button` would otherwise hand MUI's `lineHeight: 1.75` through.
 */
const BUTTON_SIZES = {
  small: { px: 1, py: 0.5, gap: 0.5 },
  medium: { px: 1.5, py: 0.75, gap: 0.75 },
  large: { px: 2, py: 1, gap: 0.75 },
} as const;

/** Figma rounds large at medium's 6px, not at the 8px its name would suggest. */
const BUTTON_RADIUS = { small: "small", medium: "medium", large: "medium" } as const;

const BUTTON_LABEL = { small: "buttonSmall", medium: "buttonMedium", large: "buttonLarge" } as const;

/**
 * Icon-only padding, in spacing multipliers. Exported because `ui/icon-button` draws the outlined
 * stroke itself and has to give its pixel back from the same number.
 */
export const ICON_BUTTON_PADDING = { small: 0.75, medium: 1, large: 1.25 } as const;

/**
 * One glyph per size, shared by the labelled slots and the icon-only square. As a `font-size` it reaches
 * `@mui/icons-material` children but not Apollo's SVGR icons, which render hardcoded `width`/`height` —
 * so `ui/button` feeds the same number through `sizeIconElement`.
 */
export const BUTTON_GLYPH = { small: 12, medium: 16, large: 20 } as const;

/** Outlined's stroke is drawn *inside* the box, so its padding gives the border's pixel back. */
const BUTTON_BORDER = 1;

/** `props.size` is typed with MUI's override union, so an unknown value falls back to medium. */
const sizeName = (size: string | undefined): ButtonSizeName =>
  size === "small" || size === "large" ? size : "medium";

const OUTLINED = "&.MuiButton-outlined";

const buttonSizeStyle = (theme: Theme, size: ButtonSizeName): CSSObject => {
  const { px, py } = BUTTON_SIZES[size];

  return {
    ...theme.typography[BUTTON_LABEL[size]],
    padding: theme.spacing(py, px),
    borderRadius: theme.shape[BUTTON_RADIUS[size]],
    [OUTLINED]: {
      padding: `calc(${theme.spacing(py)} - ${BUTTON_BORDER}px) calc(${theme.spacing(px)} - ${BUTTON_BORDER}px)`,
    },
  };
};

const PALETTE_COLORS = ["primary", "secondary", "error", "info", "success", "warning"] as const;

/**
 * Every variant steps to `dark` on hover. MUI already does that for `contained`; `outlined` and `text`
 * keep their label colour, so the label steps here. `text` keeps MUI's own `alpha(main, hoverOpacity)`
 * tint on top of it; `outlined` has none because its surface is set outright (`styleOverrides.outlined`).
 */
const paletteColorVariants = PALETTE_COLORS.map((color) => ({
  props: { color },
  style: ({ theme }: { theme: Theme }) => ({
    // MUI strokes the outlined border at `alpha(main, 0.5)`; the design strokes it in the label colour.
    "--variant-outlinedBorder": "currentColor",
    "@media (hover: hover)": {
      "&:hover": {
        "--variant-textColor": theme.palette[color].dark,
        "--variant-outlinedColor": theme.palette[color].dark,
        "--variant-outlinedBorder": "currentColor",
      },
    },
  }),
}));

/**
 * `inherit` is the design's neutral colour rather than CSS inheritance. MUI's `inherit` rule sets
 * `color: inherit` *after* the per-variant `color: var(…)` rules, so the label is restated per variant
 * here instead of through the custom properties the palette colours use.
 */
const inheritVariants = [
  {
    props: { color: "inherit" },
    style: ({ theme }: { theme: Theme }) => ({
      "--variant-containedBg": theme.palette.grey[600],
      "@media (hover: hover)": {
        "&:hover": { "--variant-containedBg": theme.palette.grey[900] },
      },
    }),
  },
  {
    props: { color: "inherit", variant: "contained" },
    style: ({ theme }: { theme: Theme }) => ({ color: theme.palette.common.white }),
  },
  ...(["outlined", "text"] as const).map((variant) => ({
    props: { color: "inherit", variant },
    style: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.grey[600],
      "@media (hover: hover)": { "&:hover": { color: theme.palette.grey[900] } },
    }),
  })),
];

/**
 * **One depth at rest, hover, press and disabled; one step up on focus.** MUI walks `shadows[2] → [4] →
 * [8]` with `[6]` on focus and `[0]` disabled, each on its own state selector, so every one is restated.
 * Behind `disableElevation: false` because these rules land after MUI's own, and would otherwise
 * out-rank the flat look that prop asks for.
 */
const elevationVariants = (["contained", "outlined"] as const).map((variant) => ({
  props: { variant, disableElevation: false },
  style: ({ theme }: { theme: Theme }) => ({
    boxShadow: theme.shadows[1],
    "&:hover": {
      boxShadow: theme.shadows[1],
      "@media (hover: none)": { boxShadow: theme.shadows[1] },
    },
    "&:active": { boxShadow: theme.shadows[1] },
    "&.Mui-focusVisible": { boxShadow: theme.shadows[2] },
    "&.Mui-disabled": { boxShadow: theme.shadows[1] },
  }),
}));

export const MuiButton: ComponentOverrides["MuiButton"] = {
  styleOverrides: {
    root: ({ theme }) => ({
      // MUI pads out to `min-width: 64px`; the design hugs the label.
      minWidth: 0,
      "&.Mui-disabled": { color: theme.palette.grey[500] },
      variants: [
        ...paletteColorVariants,
        ...inheritVariants,
        ...elevationVariants,
        // MUI buttons take their width from the parent, so one in a `flex-col` stretches to the
        // whole column. Skipped for `fullWidth` so the prop still wins.
        { props: { fullWidth: false }, style: { width: "fit-content" } },
      ],
    }),

    /**
     * Slot keys rather than `root.variants`, which is where MUI keeps its per-size padding — every
     * `styleOverrides` key lands after it. Outlined nests because v9 removed `outlinedSize*` and
     * `root.variants` emits `root` before `size…`, losing to `sizeSmall` below.
     */
    sizeSmall: ({ theme }) => buttonSizeStyle(theme, "small"),
    sizeMedium: ({ theme }) => buttonSizeStyle(theme, "medium"),
    sizeLarge: ({ theme }) => buttonSizeStyle(theme, "large"),

    contained: ({ theme }) => ({
      "&.Mui-disabled": { backgroundColor: theme.palette.grey[100] },
    }),

    /**
     * The surface stays `background.paper` in every state, where MUI leaves it transparent and tints it
     * on hover through `--variant-outlinedBg` — this rule stops reading that variable at all.
     */
    outlined: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      "&.Mui-disabled": { borderColor: theme.palette.grey[200] },
    }),

    // MUI pulls the glyph back over the button's own padding, so it would hang outside a text button
    // whose padding a call site has zeroed.
    startIcon: ({ ownerState, theme }) => {
      const size = sizeName(ownerState.size);

      return {
        marginLeft: 0,
        marginRight: theme.spacing(BUTTON_SIZES[size].gap),
        "& > *:nth-of-type(1)": { fontSize: BUTTON_GLYPH[size] },
      };
    },
    endIcon: ({ ownerState, theme }) => {
      const size = sizeName(ownerState.size);

      return {
        marginRight: 0,
        marginLeft: theme.spacing(BUTTON_SIZES[size].gap),
        "& > *:nth-of-type(1)": { fontSize: BUTTON_GLYPH[size] },
      };
    },
  },
};

/**
 * Padding and glyph only. The icon-only radius and surfaces are `ui/icon-button`'s, so the icon
 * buttons MUI renders inside `Autocomplete` and `DateCalendar` keep their own round shape.
 */
export const MuiIconButton: ComponentOverrides["MuiIconButton"] = {
  styleOverrides: {
    sizeSmall: ({ theme }) => ({
      padding: theme.spacing(ICON_BUTTON_PADDING.small),
      fontSize: BUTTON_GLYPH.small,
    }),
    sizeMedium: ({ theme }) => ({
      padding: theme.spacing(ICON_BUTTON_PADDING.medium),
      fontSize: BUTTON_GLYPH.medium,
    }),
    sizeLarge: ({ theme }) => ({
      padding: theme.spacing(ICON_BUTTON_PADDING.large),
      fontSize: BUTTON_GLYPH.large,
    }),
  },
};
